import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { StickyNote, Plus, X } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { slug: "", label: "All" },
  { slug: "general", label: "General" },
  { slug: "for_sale", label: "For sale" },
  { slug: "wanted", label: "Wanted" },
  { slug: "housing", label: "Housing" },
  { slug: "lost_found", label: "Lost & found" },
  { slug: "services", label: "Services" },
  { slug: "community", label: "Community" },
];

export default function Noticeboard() {
  const [category, setCategory] = useState("");
  const [notices, setNotices] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "general", author_name: "", contact: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    const q = category ? `?category=${category}` : "";
    api.get(`/noticeboard${q}`).then((r) => setNotices(r.data)).catch(() => setNotices([]));
  };
  useEffect(load, [category]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return toast.error("Add a title and message.");
    setBusy(true);
    try {
      await api.post("/noticeboard", form);
      toast.success("Thanks! Your notice is submitted for review.");
      setForm({ title: "", body: "", category: "general", author_name: "", contact: "" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit notice.");
    } finally {
      setBusy(false);
    }
  };

  const prettyCat = (slug) => CATEGORIES.find((c) => c.slug === slug)?.label || slug;

  return (
    <Layout>
      <section className="bg-navy text-cream py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 text-xs tracking-widest text-sponsored uppercase mb-3">
            <StickyNote className="w-4 h-4" /> Community Noticeboard
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <h1 className="font-heading text-4xl lg:text-6xl tracking-tight">
                Airlie's <em className="text-sponsored">noticeboard</em>
              </h1>
              <p className="text-cream/70 mt-3 max-w-2xl">
                Lost keys, spare rooms, ute for sale, kite lessons — post a notice and reach the
                whole community.
              </p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 bg-sponsored text-navy px-5 py-3 rounded-full text-sm font-medium hover:bg-sponsored/90"
              data-testid="post-notice-btn"
            >
              <Plus className="w-4 h-4" /> Post a notice
            </button>
          </div>
        </div>
      </section>

      <section className="bg-cream py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap gap-2 mb-8" data-testid="notice-cats">
            {CATEGORIES.map((c) => {
              const active = c.slug === category;
              return (
                <button
                  key={c.slug || "all"}
                  onClick={() => setCategory(c.slug)}
                  className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                    active
                      ? "bg-navy border-navy text-cream"
                      : "bg-white border-black/5 text-navy hover:border-navy"
                  }`}
                  data-testid={`notice-cat-${c.slug || "all"}`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          {notices === null ? (
            <div className="text-neutral-500">Loading…</div>
          ) : notices.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 border border-black/5 text-center">
              <StickyNote className="w-8 h-8 text-navy/40 mx-auto mb-3" />
              <h2 className="font-heading text-2xl text-navy">No notices yet</h2>
              <p className="text-neutral-500 mt-2">Be the first to post — tap "Post a notice".</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="notice-list">
              {notices.map((n, i) => (
                <motion.article
                  key={n.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03, duration: 0.3 }}
                  className="bg-white rounded-2xl border border-black/5 p-5"
                  data-testid={`notice-${n.id}`}
                >
                  <div className="text-[10px] tracking-widest text-coastal uppercase">
                    {prettyCat(n.category)} ·{" "}
                    {new Date(n.created_at).toLocaleDateString("en-AU", {
                      day: "numeric",
                      month: "short",
                    })}
                  </div>
                  <h3 className="font-heading text-xl text-navy mt-1">{n.title}</h3>
                  <p className="text-neutral-700 mt-2 whitespace-pre-line text-sm leading-relaxed">
                    {n.body}
                  </p>
                  {(n.author_name || n.contact) && (
                    <div className="mt-4 pt-4 border-t border-black/5 text-xs text-neutral-500">
                      {n.author_name ? <div>— {n.author_name}</div> : null}
                      {n.contact ? <div className="text-navy mt-1">{n.contact}</div> : null}
                    </div>
                  )}
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {showForm && (
        <div className="fixed inset-0 bg-navy/60 flex items-end md:items-center justify-center z-50 p-4">
          <form
            onSubmit={submit}
            className="bg-cream rounded-2xl p-6 w-full max-w-lg max-h-[92vh] overflow-y-auto"
            data-testid="notice-form"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading text-2xl text-navy">Post a notice</h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-navy p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border border-black/10 rounded-lg px-3 py-2.5 bg-white"
                data-testid="notice-category"
              >
                {CATEGORIES.filter((c) => c.slug).map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.label}
                  </option>
                ))}
              </select>
              <input
                required
                placeholder="Title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full border border-black/10 rounded-lg px-3 py-2.5 bg-white"
                data-testid="notice-title"
              />
              <textarea
                required
                rows={5}
                placeholder="Your message…"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="w-full border border-black/10 rounded-lg px-3 py-2.5 bg-white"
                data-testid="notice-body"
              />
              <input
                placeholder="Your name (optional)"
                value={form.author_name}
                onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                className="w-full border border-black/10 rounded-lg px-3 py-2.5 bg-white"
                data-testid="notice-name"
              />
              <input
                placeholder="Contact (phone / email / IG — optional)"
                value={form.contact}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
                className="w-full border border-black/10 rounded-lg px-3 py-2.5 bg-white"
                data-testid="notice-contact"
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full bg-navy text-cream py-3 rounded-full font-medium disabled:opacity-50"
                data-testid="notice-submit"
              >
                {busy ? "Sending…" : "Submit for review"}
              </button>
              <p className="text-xs text-neutral-500 text-center">
                Notices are moderated to keep the board useful. Approved posts appear within a
                day.
              </p>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
